import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';

// --- Типизация (оставляем вашу, она корректна) ---
interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  secondary_bg_color?: string;
  hint_color?: string;
  button_color?: string;
}

interface TelegramMainButton {
  setText: (text: string) => void;
  show: () => void;
  onClick: (handler: () => void) => void;
  offClick?: (handler: () => void) => void;
  off?: (handler: () => void) => void;
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  isExpanded?: boolean;
  sendData: (data: string) => void;
  themeParams?: TelegramThemeParams;
  MainButton: TelegramMainButton;
  viewportHeight?: number;
  viewportStableHeight?: number;
  onEvent?: (event: string, handler: () => void) => void;
  offEvent?: (event: string, handler: () => void) => void;
  requestFullscreen?: () => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

const MAIN_BUTTON_TEXT = 'Написать в бот';
const FALLBACK_NAME = 'HELENENDORFSKY';
const FALLBACK_BIO = 'Building the future through scalable technology and strategic innovation.';

const TelegramApp: React.FC = () => {
  const { profile } = useData();
  const [viewportHeight, setViewportHeight] = useState<number | string>('100vh');

  // Получаем доступ к объекту Telegram безопасным способом
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

  useEffect(() => {
    if (!tg) return;

    // 1. Additional expand/requestFullscreen fallback after mounting (prefer the newer requestFullscreen, then expand)
    let expandFallbackTimeout: number | undefined;
    if (!tg.isExpanded) {
      if (tg.requestFullscreen) {
        tg.requestFullscreen();
        expandFallbackTimeout = window.setTimeout(() => {
          if (!tg.isExpanded) tg.expand?.();
        }, 150);
      } else {
        tg.expand?.();
      }
    }

    // 2. Функция для синхронизации высоты приложения с высотой окна Telegram
    const syncHeight = () => {
      // Используем viewportStableHeight, если он есть, для предотвращения прыжков при скролле
      const vh = tg.viewportStableHeight || tg.viewportHeight || window.innerHeight;
      setViewportHeight(vh);
      // Дублируем в CSS переменную для надежности
      document.documentElement.style.setProperty('--tg-viewport-height', `${vh}px`);
    };

    // 3. Вызываем синхронизацию сразу
    syncHeight();

    // 4. Подписываемся на изменение размеров (например, при раскрытии клавиатуры или самого окна)
    tg.onEvent?.('viewportChanged', syncHeight);

    // 5. Сообщаем Telegram, что приложение полностью загрузилось
    tg.ready();

    // Очистка слушателей при размонтировании
    return () => {
      if (expandFallbackTimeout) window.clearTimeout(expandFallbackTimeout);
      tg.offEvent?.('viewportChanged', syncHeight);
    };
  }, [tg]);

  // Логика кнопки MainButton
  useEffect(() => {
    if (!tg) return;
    tg.MainButton.setText(MAIN_BUTTON_TEXT);
    tg.MainButton.show();
    
    const handler = () => tg.sendData(JSON.stringify({ action: 'contact', from: 'tg-web-app' }));
    tg.MainButton.onClick(handler);
    
    return () => {
      tg.MainButton.offClick?.(handler);
    };
  }, [tg]);

  // Стили и данные
  const theme = tg?.themeParams || {};
  const bgColor = theme.bg_color || '#0b0b0f';
  const textColor = theme.text_color || '#ffffff';
  const cardBg = theme.secondary_bg_color || 'rgba(255,255,255,0.04)';
  const hintColor = theme.hint_color || 'rgba(255,255,255,0.75)';
  const accentColor = theme.button_color || '#4c83ff';

  const socials = [
    { label: 'Telegram', value: profile?.socials?.telegram },
    { label: 'LinkedIn', value: profile?.socials?.linkedin },
    { label: 'GitHub', value: profile?.socials?.github },
    { label: 'Twitter', value: profile?.socials?.twitter }
  ].filter((link): link is { label: string; value: string } => Boolean(link.value));

  return (
    <div
      style={{
        // Используем вычисленную высоту
        minHeight: typeof viewportHeight === 'number' ? `${viewportHeight}px` : viewportHeight,
        height: typeof viewportHeight === 'number' ? `${viewportHeight}px` : viewportHeight,
        backgroundColor: bgColor,
        color: textColor,
        padding: '16px',
        boxSizing: 'border-box',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        overflowY: 'auto' // Добавляем скролл внутри контейнера, если контент не влезает
      }}
    >
      <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        
        {/* Profile Card */}
        <div
          style={{
            padding: '20px',
            borderRadius: '14px',
            backgroundColor: cardBg,
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '14px', letterSpacing: '0.08em', textTransform: 'uppercase', color: hintColor }}>Profile</div>
            <div style={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.1 }}>
              {profile?.name || FALLBACK_NAME}
            </div>
            <div style={{ fontSize: '14px', color: hintColor, lineHeight: 1.6 }}>
              {profile?.bio || FALLBACK_BIO}
            </div>
          </div>
        </div>

        {/* Skills Card */}
        <div
          style={{
            padding: '16px',
            borderRadius: '14px',
            backgroundColor: cardBg,
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', letterSpacing: '0.04em', textTransform: 'uppercase', color: hintColor }}>
            Skills
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {(profile?.skills && profile.skills.length > 0 ? profile.skills : ['Strategy', 'Tech', 'Product']).map((skill) => (
              <span
                key={skill}
                style={{
                  padding: '6px 10px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  color: textColor,
                  fontSize: '12px',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Socials Card */}
        <div
          style={{
            padding: '16px',
            borderRadius: '14px',
            backgroundColor: cardBg,
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: hintColor }}>Socials</div>
          {socials.length === 0 ? (
            <div style={{ fontSize: '13px', color: hintColor }}>No social links available.</div>
          ) : (
            socials.map((link) => (
              <a
                key={link.label}
                href={link.value}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  color: textColor,
                  textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                <span style={{ fontSize: '14px' }}>{link.label}</span>
                <span style={{ fontSize: '12px', color: accentColor }}>{link.value}</span>
              </a>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TelegramApp;
